import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import { useAuth } from '../context/AuthContext';

const SPORTS = ['NBA', 'EPL', 'MLS', 'MLB'];

const JOIN_POLICY_LABELS = {
  open:         { label: 'Open',        icon: 'fa-globe',     color: 'var(--green)' },
  request:      { label: 'Request',     icon: 'fa-hand-o-up', color: 'var(--gold)' },
  invite_only:  { label: 'Invite Only', icon: 'fa-lock',      color: 'var(--text-dim)' },
};

function GroupAvatar({ name, avatarUrl, size = 48 }) {
  const [err, setErr] = useState(false);
  const initials = (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  if (avatarUrl && !err) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="grp-avatar grp-avatar--img"
        style={{ width: size, height: size }}
        onError={() => setErr(true)}
      />
    );
  }
  return (
    <div className="grp-avatar" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {initials}
    </div>
  );
}

function GroupCard({ group, myRole, onView, onJoin, joining }) {
  const jp   = JOIN_POLICY_LABELS[group.join_policy] || JOIN_POLICY_LABELS.open;
  const full = group.max_members && group.member_count >= group.max_members;

  return (
    <div className="grp-card" onClick={() => onView(group.id)}>
      <div className="grp-card-top">
        <GroupAvatar name={group.name} avatarUrl={group.avatar_url} size={52} />
        <div className="grp-card-info">
          <span className="grp-card-name">{group.name}</span>
          <div className="grp-card-meta">
            {group.sport_filter && (
              <span className="grp-badge grp-badge--sport">{group.sport_filter}</span>
            )}
            <span className="grp-badge" style={{ color: jp.color }}>
              <i className={`fa ${jp.icon}`} style={{ marginRight: 4 }} />{jp.label}
            </span>
          </div>
        </div>
        {myRole && (
          <span className={`grp-role-pill grp-role-pill--${myRole}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {myRole === 'owner' && <i className="fa fa-crown" style={{ marginRight: 4 }} />}
            {myRole === 'admin' && <i className="fa fa-shield" style={{ marginRight: 4 }} />}
            {myRole}
          </span>
        )}
      </div>
      {group.description && (
        <p className="grp-card-desc">{group.description}</p>
      )}
      <div className="grp-card-footer">
        <span className="grp-card-stat">
          <i className="fa fa-users" /> {group.member_count}{group.max_members ? `/${group.max_members}` : ''} members
        </span>
        <div className="grp-card-actions" onClick={e => e.stopPropagation()}>
          <button className="grp-btn grp-btn--outline" onClick={() => onView(group.id)}>
            View
          </button>
          {!myRole && !full && group.join_policy !== 'invite_only' && (
            <button
              className="grp-btn grp-btn--primary"
              onClick={() => onJoin(group)}
              disabled={joining === group.id}
            >
              {joining === group.id ? <i className="fa fa-spinner fa-spin" /> : (
                group.join_policy === 'request' ? 'Request' : 'Join'
              )}
            </button>
          )}
          {!myRole && full && (
            <span className="grp-badge" style={{ color: 'var(--red)' }}>Full</span>
          )}
        </div>
      </div>
    </div>
  );
}

function InviteCard({ invite, onAccept, onDecline, processing }) {
  return (
    <div className="grp-invite-card">
      <GroupAvatar name={invite.group_name} avatarUrl={invite.group_avatar_url} size={44} />
      <div className="grp-invite-info">
        <span className="grp-invite-name">{invite.group_name}</span>
        <span className="grp-invite-from">
          Invited by <strong>@{invite.inviter_username}</strong>
          {invite.sport_filter && <> · <span className="grp-badge grp-badge--sport" style={{ marginLeft: 4 }}>{invite.sport_filter}</span></>}
          {' '}· {invite.member_count} members
        </span>
      </div>
      <div className="grp-invite-actions">
        <button
          className="grp-btn grp-btn--accept"
          onClick={() => onAccept(invite)}
          disabled={processing === invite.invite_id}
        >
          {processing === invite.invite_id ? <i className="fa fa-spinner fa-spin" /> : 'Accept'}
        </button>
        <button
          className="grp-btn grp-btn--decline"
          onClick={() => onDecline(invite)}
          disabled={processing === invite.invite_id}
        >
          Decline
        </button>
      </div>
    </div>
  );
}

const DEFAULT_FORM = {
  name: '', description: '', is_public: true, join_policy: 'open',
  max_members: 50, sport_filter: '',
};

export default function GroupsPage() {
  const { authData } = useAuth();
  const navigate     = useNavigate();
  const token        = authData?.token;
  const headers      = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [activeTab,   setActiveTab]   = useState('mine');
  const [myGroups,    setMyGroups]    = useState([]);
  const [myInvites,   setMyInvites]   = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [publicTotal, setPublicTotal] = useState(0);
  const [publicPage,  setPublicPage]  = useState(1);
  const [loadingMine, setLoadingMine] = useState(true);
  const [loadingPub,  setLoadingPub]  = useState(false);

  const [discoverQ,       setDiscoverQ]       = useState('');
  const [discoverSport,   setDiscoverSport]   = useState('');
  const [joining,         setJoining]         = useState(null);
  const [processingInvite, setProcessingInvite] = useState(null);
  const [joinFeedback,    setJoinFeedback]    = useState(null); // {id, msg, ok}

  const [showCreate, setShowCreate] = useState(false);
  const [form,       setForm]       = useState(DEFAULT_FORM);
  const [creating,   setCreating]   = useState(false);
  const [createErr,  setCreateErr]  = useState('');

  const searchTimer = useRef(null);

  // ── Data fetching ────────────────────────────────────────────────────────
  const fetchMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      const [grpRes, invRes] = await Promise.all([
        axios.get(`${API_BASE}/groups/my-groups`,  { headers }),
        axios.get(`${API_BASE}/groups/my-invites`, { headers }),
      ]);
      setMyGroups(grpRes.data.groups || []);
      setMyInvites(invRes.data.invites || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMine(false);
    }
  }, [headers]); // fetchMine

  const fetchPublic = useCallback(async (q = discoverQ, sport = discoverSport, page = 1) => {
    setLoadingPub(true);
    try {
      const res = await axios.get(`${API_BASE}/groups/`, {
        headers,
        params: { q, sport, page },
      });
      setPublicGroups(page === 1 ? res.data.groups : prev => [...prev, ...(res.data.groups || [])]);
      setPublicTotal(res.data.total || 0);
      setPublicPage(page);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPub(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]); // fetchPublic — q/sport/page always passed explicitly as args

  useEffect(() => {
    document.body.classList.add('account-page-bg');
    fetchMine();
    fetchPublic('', '', 1);
    return () => document.body.classList.remove('account-page-bg');
  }, [fetchMine, fetchPublic]);

  // ── Discover search/filter ───────────────────────────────────────────────
  const handleDiscoverSearch = (val) => {
    setDiscoverQ(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchPublic(val, discoverSport, 1), 350);
  };

  const handleSportFilter = (sport) => {
    const next = sport === discoverSport ? '' : sport;
    setDiscoverSport(next);
    fetchPublic(discoverQ, next, 1);
  };

  // ── Join ─────────────────────────────────────────────────────────────────
  const handleJoin = async (group) => {
    setJoining(group.id);
    try {
      const res = await axios.post(`${API_BASE}/groups/${group.id}/join`, {}, { headers });
      setJoinFeedback({ id: group.id, msg: res.data.message, ok: true });
      fetchMine();
      fetchPublic(discoverQ, discoverSport, 1);
    } catch (e) {
      setJoinFeedback({ id: group.id, msg: e.response?.data?.error || 'Failed to join', ok: false });
    } finally {
      setJoining(null);
    }
  };

  // ── Invite responds ──────────────────────────────────────────────────────
  const handleInviteRespond = async (invite, action) => {
    setProcessingInvite(invite.invite_id);
    try {
      const res = await axios.post(
        `${API_BASE}/groups/invites/${invite.invite_id}/respond`,
        { action },
        { headers }
      );
      setMyInvites(prev => prev.filter(i => i.invite_id !== invite.invite_id));
      if (action === 'accept') {
        fetchMine();
        navigate(`/groups/${res.data.group_id}`);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setProcessingInvite(null);
    }
  };

  // ── Create group ─────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateErr('');
    if (!form.name.trim()) { setCreateErr('Group name is required'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API_BASE}/groups/create`, {
        ...form,
        max_members: parseInt(form.max_members) || 50,
        sport_filter: form.sport_filter || null,
      }, { headers });
      setShowCreate(false);
      setForm(DEFAULT_FORM);
      navigate(`/groups/${res.data.group_id}`);
    } catch (e) {
      setCreateErr(e.response?.data?.error || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const tabs = [
    { id: 'mine',     label: 'My Groups',  icon: 'fa-users' },
    { id: 'discover', label: 'Discover',   icon: 'fa-globe' },
  ];

  return (
    <BaseLayout>
      <div className="grp-wrap">
        {/* Header */}
        <div className="grp-page-header">
          <div>
            <h1 className="grp-page-title">Groups</h1>
            <p className="grp-page-subtitle">Compete with friends in private pick-em leagues</p>
          </div>
          <button className="grp-btn grp-btn--primary grp-create-btn" onClick={() => setShowCreate(true)}>
            <i className="fa fa-plus" /> Create Group
          </button>
        </div>

        {/* Pending invites banner */}
        {myInvites.length > 0 && (
          <div className="grp-invites-section">
            <h3 className="grp-section-label">
              <i className="fa fa-envelope-o" /> Pending Invites
              <span className="grp-count-pill">{myInvites.length}</span>
            </h3>
            <div className="grp-invites-list">
              {myInvites.map(inv => (
                <InviteCard
                  key={inv.invite_id}
                  invite={inv}
                  onAccept={i => handleInviteRespond(i, 'accept')}
                  onDecline={i => handleInviteRespond(i, 'decline')}
                  processing={processingInvite}
                />
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="grp-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              className={`grp-tab${activeTab === t.id ? ' grp-tab--active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <i className={`fa ${t.icon}`} /> {t.label}
              {t.id === 'mine' && myGroups.length > 0 && (
                <span className="grp-tab-count">{myGroups.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* My Groups */}
        {activeTab === 'mine' && (
          <div className="grp-panel">
            {loadingMine ? (
              <div className="grp-loading"><FlameLoader text="Loading groups..." /></div>
            ) : myGroups.length === 0 ? (
              <div className="grp-empty">
                <i className="fa fa-users grp-empty-icon" />
                <p>You haven't joined any groups yet.</p>
                <button className="grp-btn grp-btn--primary" onClick={() => setShowCreate(true)}>
                  Create your first group
                </button>
              </div>
            ) : (
              <div className="grp-grid">
                {myGroups.map(g => (
                  <GroupCard
                    key={g.id}
                    group={g}
                    myRole={g.my_role}
                    onView={id => navigate(`/groups/${id}`)}
                    onJoin={handleJoin}
                    joining={joining}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Discover */}
        {activeTab === 'discover' && (
          <div className="grp-panel">
            <div className="grp-discover-bar">
              <div className="grp-search-wrap">
                <i className="fa fa-search grp-search-icon" />
                <input
                  className="grp-search-input"
                  placeholder="Search groups…"
                  value={discoverQ}
                  onChange={e => handleDiscoverSearch(e.target.value)}
                />
              </div>
              <div className="grp-sport-filters">
                {SPORTS.map(s => (
                  <button
                    key={s}
                    className={`grp-filter-chip${discoverSport === s ? ' grp-filter-chip--active' : ''}`}
                    onClick={() => handleSportFilter(s)}
                  >
                    {s}
                  </button>
                ))}
                {discoverSport && (
                  <button className="grp-filter-chip grp-filter-chip--clear" onClick={() => handleSportFilter(discoverSport)}>
                    <i className="fa fa-times" /> Clear
                  </button>
                )}
              </div>
            </div>

            {joinFeedback && (
              <div className={`grp-feedback${joinFeedback.ok ? ' grp-feedback--ok' : ' grp-feedback--err'}`}>
                {joinFeedback.msg}
              </div>
            )}

            {loadingPub && publicPage === 1 ? (
              <div className="grp-loading"><FlameLoader text="Loading groups..." /></div>
            ) : publicGroups.length === 0 ? (
              <div className="grp-empty">
                <i className="fa fa-search grp-empty-icon" />
                <p>No public groups found.</p>
              </div>
            ) : (
              <>
                <p className="grp-result-count">{publicTotal} public group{publicTotal !== 1 ? 's' : ''}</p>
                <div className="grp-grid">
                  {publicGroups.map(g => {
                    const mine = myGroups.find(m => m.id === g.id);
                    return (
                      <GroupCard
                        key={g.id}
                        group={g}
                        myRole={mine?.my_role || null}
                        onView={id => navigate(`/groups/${id}`)}
                        onJoin={handleJoin}
                        joining={joining}
                      />
                    );
                  })}
                </div>
                {publicPage * 12 < publicTotal && (
                  <button
                    className="grp-load-more"
                    onClick={() => fetchPublic(discoverQ, discoverSport, publicPage + 1)}
                    disabled={loadingPub}
                  >
                    {loadingPub ? <i className="fa fa-spinner fa-spin" /> : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      {showCreate && (
        <div className="grp-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="grp-modal" onClick={e => e.stopPropagation()}>
            <div className="grp-modal-header">
              <span className="grp-modal-title">Create a Group</span>
              <button className="grp-modal-close" onClick={() => setShowCreate(false)}>
                <i className="fa fa-times" />
              </button>
            </div>
            <form className="grp-modal-body" onSubmit={handleCreate}>
              <label className="grp-label">Group Name *</label>
              <input
                className="grp-input"
                maxLength={50}
                placeholder="e.g. The Office Picks"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
              />

              <label className="grp-label">Description</label>
              <textarea
                className="grp-input grp-textarea"
                maxLength={200}
                rows={3}
                placeholder="What's this group about?"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />

              <div className="grp-form-row">
                <div className="grp-form-col">
                  <label className="grp-label">Sport Focus</label>
                  <select
                    className="grp-input grp-select"
                    value={form.sport_filter}
                    onChange={e => setForm(f => ({ ...f, sport_filter: e.target.value }))}
                  >
                    <option value="">All Sports</option>
                    {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grp-form-col">
                  <label className="grp-label">Max Members</label>
                  <input
                    className="grp-input"
                    type="number"
                    min={2}
                    max={100}
                    value={form.max_members}
                    onChange={e => setForm(f => ({ ...f, max_members: e.target.value }))}
                  />
                </div>
              </div>

              <label className="grp-label">Join Policy</label>
              <div className="grp-radio-group">
                {[
                  { v: 'open',        label: 'Open',        desc: 'Anyone can join' },
                  { v: 'request',     label: 'Request',     desc: 'Members must be approved' },
                  { v: 'invite_only', label: 'Invite Only', desc: 'By invite link or direct invite only' },
                ].map(opt => (
                  <label key={opt.v} className={`grp-radio-opt${form.join_policy === opt.v ? ' grp-radio-opt--active' : ''}`}>
                    <input
                      type="radio"
                      name="join_policy"
                      value={opt.v}
                      checked={form.join_policy === opt.v}
                      onChange={() => setForm(f => ({ ...f, join_policy: opt.v }))}
                    />
                    <div>
                      <span className="grp-radio-label">{opt.label}</span>
                      <span className="grp-radio-desc">{opt.desc}</span>
                    </div>
                  </label>
                ))}
              </div>

              <label className="grp-label">Visibility</label>
              <div className="grp-toggle-row">
                <button
                  type="button"
                  className={`grp-toggle-btn${form.is_public ? ' grp-toggle-btn--active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, is_public: true }))}
                >
                  <i className="fa fa-globe" /> Public
                </button>
                <button
                  type="button"
                  className={`grp-toggle-btn${!form.is_public ? ' grp-toggle-btn--active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, is_public: false }))}
                >
                  <i className="fa fa-lock" /> Private
                </button>
              </div>

              {createErr && <p className="grp-form-error">{createErr}</p>}

              <div className="grp-modal-footer">
                <button type="button" className="grp-btn grp-btn--outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="grp-btn grp-btn--primary" disabled={creating}>
                  {creating ? <i className="fa fa-spinner fa-spin" /> : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </BaseLayout>
  );
}
